import { createSlice } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";
import type { Exact, ExactArrayItems } from "@Helpers/types/exact";
import type {
  AdditionalCostItem,
  EstimateData,
  EstimateListItem,
  EstimateVersionEntry,
} from "@ServerTypes/estimates/estimates.schema.js";

/**
 * The open estimate as `/estimate/getEstimateData` sent it, or `{}`: the slice is empty until that answer lands and again
 * once Estimate.tsx clears it. Never part of an estimate, so a member the route always sends (`_id`) tells the two apart.
 */
export type EstimateDataState = EstimateData | { [Member in keyof EstimateData]?: never };

// A function, so the slice's state is the whole union: a constant initialised with `{}` reads as the empty arm alone.
const noEstimateData = (): EstimateDataState => ({});

/** Which estimate panels must refetch after a takeoff or measurement change. */
export type EstimateFieldsNeedToRefresh = {
  takeoffs: string[];
  measurements: boolean;
};

export const estimateFieldsNeedToRefreshIntialState: EstimateFieldsNeedToRefresh = {
  takeoffs: [],
  measurements: false,
};

const estimateListSlice = createSlice({
  name: "estimateList",
  initialState: [] as EstimateListItem[],
  reducers: {
    getEstimateList: (_state, action: PayloadAction<EstimateListItem[]>) => action.payload,
    emptyEstimateList: () => [],
  },
});

const estimateErrorSlice = createSlice({
  name: "estimateError",
  initialState: false as boolean | number,
  reducers: {
    setEstimateError: (_state, action: PayloadAction<boolean | number>) => action.payload,
  },
});

const estimateAdditionalCostSlice = createSlice({
  name: "estimateAdditionalCost",
  initialState: [] as AdditionalCostItem[],
  reducers: {
    setAdditionalCost: (_state, action: PayloadAction<AdditionalCostItem[]>) => action.payload,
  },
});

const additionalCostTotalSlice = createSlice({
  name: "additionalCostTotal",
  initialState: 0,
  reducers: {
    setEstimateAdditionalCostTotal: (_state, action: PayloadAction<number>) => action.payload,
  },
});

const dateVersionSlice = createSlice({
  name: "dateVersion",
  initialState: [] as EstimateVersionEntry[],
  reducers: {
    getEstimateDateversion: (_state, action: PayloadAction<EstimateVersionEntry[]>) => action.payload,
  },
});

const estimateDataSlice = createSlice({
  name: "estimateData",
  initialState: noEstimateData,
  reducers: {
    setEstimateData: (_state, action: PayloadAction<EstimateDataState>) => action.payload,
  },
});

const estimateFieldsNeedToRefreshSlice = createSlice({
  name: "estimateFieldsNeedToRefresh",
  initialState: estimateFieldsNeedToRefreshIntialState,
  reducers: {
    setEstimateFieldsNeedtoRefresh: (_state, action: PayloadAction<EstimateFieldsNeedToRefresh>) => action.payload,
  },
});

const { getEstimateList: _getEstimateListRaw, emptyEstimateList } = estimateListSlice.actions;
const { setAdditionalCost: _setAdditionalCostRaw } = estimateAdditionalCostSlice.actions;
export const { setEstimateAdditionalCostTotal } = additionalCostTotalSlice.actions;
const { getEstimateDateversion: _getEstimateDateversionRaw } = dateVersionSlice.actions;
const { setEstimateData: _setEstimateDataRaw } = estimateDataSlice.actions;
const { setEstimateFieldsNeedtoRefresh: _setEstimateFieldsNeedtoRefreshRaw } = estimateFieldsNeedToRefreshSlice.actions;
export const { setEstimateError } = estimateErrorSlice.actions;
export { emptyEstimateList };

/**
 * Object- and array-payload action creators wrapped with `Exact<>` /
 * `ExactArrayItems<>` so excess properties are caught even when the payload
 * comes through a variable. Array wraps run per-element checking.
 */
export const setEstimateData = <T extends Exact<EstimateDataState, T>>(payload: T) => _setEstimateDataRaw(payload);
export const setEstimateFieldsNeedtoRefresh = <T extends Exact<EstimateFieldsNeedToRefresh, T>>(payload: T) =>
  _setEstimateFieldsNeedtoRefreshRaw(payload);
export const getEstimateList = <T extends readonly EstimateListItem[]>(
  payload: T & ExactArrayItems<EstimateListItem, T>,
) => _getEstimateListRaw([...payload]);
export const setAdditionalCost = <T extends readonly AdditionalCostItem[]>(
  payload: T & ExactArrayItems<AdditionalCostItem, T>,
) => _setAdditionalCostRaw([...payload]);
export const getEstimateDateversion = <T extends readonly EstimateVersionEntry[]>(
  payload: T & ExactArrayItems<EstimateVersionEntry, T>,
) => _getEstimateDateversionRaw([...payload]);

export const estimateList = estimateListSlice.reducer;
export const getEstimateAdditionalCost = estimateAdditionalCostSlice.reducer;
export const additionalCostTotal = additionalCostTotalSlice.reducer;
export const getDateVersion = dateVersionSlice.reducer;
export const estimateData = estimateDataSlice.reducer;
export const estimateFieldsNeedToRefresh = estimateFieldsNeedToRefreshSlice.reducer;
export const estimateErrorReducer = estimateErrorSlice.reducer;

export default {
  estimateList,
  getEstimateAdditionalCost,
  additionalCostTotal,
  getDateVersion,
  estimateData,
  estimateFieldsNeedToRefresh,
  estimateErrorReducer,
};
